import { ComponentPropsWithoutRef, CSSProperties, forwardRef } from "react";
import classnames from "classnames";
import styles from "./Divider.module.scss";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../internal/hooks";

/**
 * Props for the Divider component
 * @extends ComponentPropsWithoutRef<"span">
 * @extends LayoutUtilProps
 */
export type DividerProps = ComponentPropsWithoutRef<"span"> &
  LayoutUtilProps & {
    /**
     * Controls the alignment of content within the divider.
     * @default center
     */
    alignContent?: "start" | "center" | "end";
    /**
     * Controls the spacing around the divider.
     * @default "0"
     */
    spacing?:
      | "0"
      | "half"
      | "1"
      | "2"
      | "3"
      | "4"
      | "5"
      | "6"
      | "8"
      | "12"
      | 0
      | 0.5
      | 1
      | 2
      | 3
      | 4
      | 5
      | 6
      | 8
      | 12;
    /**
     * When true, renders the divider vertically instead of horizontally.
     * @default false
     */
    vertical?: boolean;
  };

/**
 * Divider component for creating visual separators between content sections.
 *
 * Features:
 * - Horizontal and vertical divider orientations
 * - Optional content display within the divider
 * - Three content alignment options (start, center, end)
 * - Configurable spacing with predefined size tokens
 * - Supports layout utilities for positioning and spacing
 * - Flexible content placement with automatic line positioning
 * - Responsive design that adapts to container width
 *
 * @example
 * <Divider spacing="2" alignContent="center">
 *   <b>Section Title</b>
 * </Divider>
 */
export const Divider = forwardRef<HTMLSpanElement, DividerProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const { alignContent, ...restOfLayoutStyles } =
      layoutStyles as CSSProperties;
    const {
      children,
      className,
      spacing = "0",
      vertical = false,
      style,
      ...rest
    } = componentProps;
    const ContainerClasses = classnames(styles["container"], className, {
      [styles[`spacing-${spacing === 0.5 ? "half" : spacing}`]]: spacing,
      [styles["vertical"]]: vertical,
      [styles["align-start"]]: alignContent === "start",
      [styles["align-end"]]: alignContent === "end",
    });

    const contentOrdering = () => {
      if (children) {
        if (alignContent === "start") {
          return (
            <>
              <span className={styles["content"]}>{children}</span>
              <hr className={styles["divider"]} />
            </>
          );
        }
        if (alignContent === "end") {
          return (
            <>
              <hr className={styles["divider"]} />
              <span className={styles["content"]}>{children}</span>
            </>
          );
        }
        return (
          <>
            <hr className={styles["divider"]} />
            <span className={styles["content"]}>{children}</span>
            <hr className={styles["divider"]} />
          </>
        );
      }
      return <hr className={styles["divider"]} />;
    };

    const styleCombined = {
      ...style,
      ...restOfLayoutStyles,
    };

    return (
      <span
        data-anv="divider"
        ref={ref}
        style={styleCombined}
        {...rest}
        className={ContainerClasses}
      >
        {contentOrdering()}
      </span>
    );
  },
);

Divider.displayName = "Divider";
